import { useEffect, useState } from "react";
import { adminApi } from "../../api/adminApi";
import { DataTable } from "../../components/DataTable";
import type { PaymentTransaction, Plan, Subscription } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";

function statusBadge(status: string) {
  const cls =
    status === "SUCCESS" || status === "ACTIVE" ? "badge badge-published" : status === "FAILED" ? "badge" : "badge badge-draft";
  const style = status === "FAILED" ? { background: "#fee2e2", color: "#991b1b" } : undefined;
  return (
    <span className={cls} style={style}>
      {status}
    </span>
  );
}

export default function Billing() {
  const { hasRole } = useAuth();
  const canSubscribe = hasRole("OWNER", "ADMIN");

  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [payingPlan, setPayingPlan] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  function load() {
    setLoading(true);
    Promise.all([
      adminApi.billing.plans(),
      adminApi.billing.subscription(),
      canSubscribe ? adminApi.billing.payments() : Promise.resolve(null),
    ])
      .then(([p, s, pay]) => {
        setPlans(p);
        setSubscription(s);
        setPayments(pay ? pay.content : []);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load billing"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  async function handleSubscribe(planCode: string) {
    if (!phoneNumber.trim()) return;
    setSubmitting(true);
    setMessage(null);
    try {
      await adminApi.billing.subscribe({ planCode, phoneNumber: phoneNumber.trim() });
      setMessage("Check your phone and enter your M-Pesa PIN to complete the payment. Your plan updates automatically once it's confirmed.");
      setPayingPlan(null);
      setPhoneNumber("");
      setTimeout(load, 5000);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Failed to start payment");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div>
      <div className="page-header">
        <h1>Billing</h1>
      </div>
      {error && <div className="error-text">{error}</div>}

      {subscription && (
        <div className="page-panel">
          <h3>Current plan</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 18, textTransform: "capitalize" }}>
              {subscription.planCode}
            </span>
            {statusBadge(subscription.status)}
            <span className="help-text">
              {subscription.status === "TRIALING" ? "Trial ends" : "Renews"} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </span>
          </div>
        </div>
      )}

      {message && (
        <div className="page-panel" style={{ borderColor: "var(--color-primary)" }}>
          <p style={{ margin: 0 }}>{message}</p>
        </div>
      )}

      <div className="report-card-grid" style={{ marginBottom: 20 }}>
        {plans.map((plan) => (
          <div className="report-card" key={plan.code}>
            <div>
              <div className="report-card-title" style={{ fontSize: 16 }}>{plan.name}</div>
              <div className="stat-card-value" style={{ fontSize: 22, marginTop: 6 }}>
                {plan.monthlyPriceKes != null ? `KES ${plan.monthlyPriceKes.toLocaleString()}` : "Custom"}
              </div>
              {plan.monthlyPriceKes != null && <div className="help-text">per month</div>}
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "var(--color-muted)" }}>
              {plan.features.map((f) => (
                <li key={f}>{f}</li>
              ))}
            </ul>

            {canSubscribe && plan.monthlyPriceKes != null && (
              payingPlan === plan.code ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <input
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    placeholder="0712345678"
                  />
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={submitting}
                      onClick={() => handleSubscribe(plan.code)}
                    >
                      {submitting ? "Sending..." : "Pay with M-Pesa"}
                    </button>
                    <button type="button" className="btn-secondary" onClick={() => setPayingPlan(null)}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn-primary" onClick={() => setPayingPlan(plan.code)}>
                  {subscription?.planCode === plan.code ? "Renew" : "Subscribe"}
                </button>
              )
            )}
            {plan.monthlyPriceKes == null && (
              <span className="help-text">Contact sales for pricing.</span>
            )}
          </div>
        ))}
      </div>

      {canSubscribe ? (
        <div className="page-panel">
          <h3>Payment history</h3>
          <DataTable
            rows={payments}
            rowKey={(p) => p.id}
            emptyMessage="No payments yet."
            columns={[
              { key: "planCode", header: "Plan" },
              { key: "amountKes", header: "Amount (KES)", render: (p) => p.amountKes.toLocaleString() },
              { key: "method", header: "Method" },
              { key: "status", header: "Status", render: (p) => statusBadge(p.status) },
              { key: "mpesaReceiptNumber", header: "Receipt", render: (p) => p.mpesaReceiptNumber ?? "-" },
              { key: "createdAt", header: "Date", render: (p) => new Date(p.createdAt).toLocaleString() },
            ]}
          />
        </div>
      ) : (
        <p className="help-text">Contact your account owner or admin to manage billing and view payment history.</p>
      )}
    </div>
  );
}
